import React, { useState, useEffect } from 'react';
import { subscribeIncompleteReports, deleteReport } from '../../services/diagnosisReports.service';
import '../../styles/admin-diagnosis.css';
import Swal from 'sweetalert2';

const IncompleteReports = () => {
    const [reports, setReports] = useState([]);
    const [filteredReports, setFilteredReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [incomeTypeFilter, setIncomeTypeFilter] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedReport, setSelectedReport] = useState(null); // For detail modal
    const [showModal, setShowModal] = useState(false);
    const itemsPerPage = 5;

    useEffect(() => {
        setLoading(true);
        const unsubscribe = subscribeIncompleteReports((data) => {
            setReports(data);
            setLoading(false);
        });
        
        return () => {
            if (unsubscribe) unsubscribe();
        };
    }, []);

    useEffect(() => {
        filterAndSortReports();
        setCurrentPage(1);
    }, [reports, searchTerm, incomeTypeFilter]);

    const filterAndSortReports = () => {
        let filtered = [...reports];

        // Search filter
        if (searchTerm) {
            filtered = filtered.filter(report =>
                report.personalInfo?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                report.personalInfo?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                report.personalInfo?.phone?.includes(searchTerm) ||
                report.reportId?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Income Type filter
        if (incomeTypeFilter !== 'all') {
            filtered = filtered.filter(report => report.personalInfo?.incomeType === incomeTypeFilter);
        }

        // Sort by date (Default)
        filtered.sort((a, b) => new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt));

        setFilteredReports(filtered);
    };

    const handleDelete = async (reportId) => {
        const result = await Swal.fire({
            title: 'Delete draft submission?',
            text: "This will permanently delete the draft information. This action cannot be undone.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                await deleteReport(reportId);
                Swal.fire('Deleted!', 'Submission draft has been deleted.', 'success');
            } catch (error) {
                console.error('Error deleting draft:', error);
                Swal.fire('Error', 'Failed to delete the draft report.', 'error');
            }
        }
    };

    const formatDate = (date) => {
        if (!date) return 'N/A';
        return new Date(date).toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatCurrency = (value) => {
        if (value === undefined || value === null || value === '') return 'N/A';
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0
        }).format(value);
    };

    const openDetailModal = (report) => {
        setSelectedReport(report);
        setShowModal(true);
    };

    const closeDetailModal = () => {
        setSelectedReport(null);
        setShowModal(false);
    };

    // Pagination calculations
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredReports.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredReports.length / itemsPerPage);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    // Helper to render populated key-value fields nicely
    const renderDataSection = (title, data) => {
        if (!data || Object.keys(data).length === 0) return null;
        
        // Filter out empty values
        const activeFields = Object.entries(data).filter(([_, val]) => val !== '' && val !== null && val !== undefined);
        if (activeFields.length === 0) return null;

        return (
            <div className="incomplete-modal-section" style={{ marginBottom: '24px', padding: '16px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <h4 style={{ color: '#1e293b', borderBottom: '2px solid #0EAB5EFF', paddingBottom: '6px', marginBottom: '12px', fontSize: '1rem', fontWeight: 600 }}>{title}</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '12px' }}>
                    {activeFields.map(([key, val]) => {
                        const formattedLabel = key
                            .replace(/([A-Z])/g, ' $1')
                            .replace(/^./, str => str.toUpperCase());
                        
                        let displayVal = val;
                        if (typeof val === 'number') {
                            displayVal = formatCurrency(val);
                        } else if (typeof val === 'object') {
                            displayVal = JSON.stringify(val);
                        }

                        return (
                            <div key={key} style={{ display: 'flex', flexDirection: 'column' }}>
                                <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 500 }}>{formattedLabel}</span>
                                <span style={{ fontSize: '13px', color: '#334155', fontWeight: 600, wordBreak: 'break-word' }}>{String(displayVal)}</span>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="admin-content-v2" style={{ display: 'flex', minHeight: '80vh' }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', flex: 1 }}>
                    <div className="custom-loader" style={{ width: '55px', height: '80px' }}>
                        <svg viewBox="0 0 55 80" xmlns="http://www.w3.org/2000/svg" fill="#0EAB5EFF">
                            <g transform="matrix(1 0 0 -1 0 80)">
                                <rect width="10" height="20" rx="3">
                                    <animate attributeName="height" begin="0s" dur="4.3s" values="20;45;57;80;64;32;66;45;64;23;66;13;64;56;34;34;2;23;76;79;20" calcMode="linear" repeatCount="indefinite" />
                                </rect>
                                <rect x="15" width="10" height="80" rx="3">
                                    <animate attributeName="height" begin="0s" dur="2s" values="80;55;33;5;75;23;73;33;12;14;60;80" calcMode="linear" repeatCount="indefinite" />
                                </rect>
                                <rect x="30" width="10" height="50" rx="3">
                                    <animate attributeName="height" begin="0s" dur="1.4s" values="50;34;78;23;56;23;34;76;80;54;21;50" calcMode="linear" repeatCount="indefinite" />
                                </rect>
                                <rect x="45" width="10" height="30" rx="3">
                                    <animate attributeName="height" begin="0s" dur="2s" values="30;45;13;80;56;72;45;76;34;23;67;30" calcMode="linear" repeatCount="indefinite" />
                                </rect>
                            </g>
                        </svg>
                    </div>
                    <p style={{ marginTop: '20px', color: '#64748B', fontWeight: 500 }}>Loading guest / incomplete submissions...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="admin-content-v2">
            <div className="diagnosis-header-v2">
                <h1 className="nav-title-v2">Incomplete Diagnosis / Leads</h1>
                <p className="nav-subtitle-v2">View and manage information filled by guest users who did not complete the payment</p>
            </div>

            {/* Stats Summary */}
            <div className="stats-summary-v2">
                <div className="stat-card-v2">
                    <span className="stat-label-v2">Total Draft Leads</span>
                    <span className="stat-value-v2">{reports.length}</span>
                    <div className="stat-trend-v2" style={{ color: '#F59E0B' }}>
                        <span>Awaiting</span>
                        <span className="stat-sub-v2">follow-up</span>
                    </div>
                </div>
                <div className="stat-card-v2">
                    <span className="stat-label-v2">Filtered View</span>
                    <span className="stat-value-v2">{filteredReports.length}</span>
                    <div className="stat-trend-v2" style={{ color: '#3B82F6' }}>
                        <span>Matching</span>
                        <span className="stat-sub-v2">criteria</span>
                    </div>
                </div>
                <div className="stat-card-v2">
                    <span className="stat-label-v2">Salary Leads</span>
                    <span className="stat-value-v2">{reports.filter(r => r.personalInfo?.incomeType === 'salary').length}</span>
                    <div className="stat-trend-v2" style={{ color: '#6366F1' }}>
                        <span>Professionals</span>
                    </div>
                </div>
                <div className="stat-card-v2">
                    <span className="stat-label-v2">Business Leads</span>
                    <span className="stat-value-v2">{reports.filter(r => ['business', 'business_partnership'].includes(r.personalInfo?.incomeType)).length}</span>
                    <div className="stat-trend-v2" style={{ color: '#0EAB5EFF' }}>
                        <span>Business Owners</span>
                    </div>
                </div>
            </div>

            {/* Filters Bar */}
            <div className="filters-bar-v2">
                <div className="search-wrapper-v2">
                    <input
                        type="text"
                        className="search-input-v2"
                        placeholder="Search by name, email, phone..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                <div className="filter-controls-v2">
                    <select className="filter-select-v2" value={incomeTypeFilter} onChange={(e) => setIncomeTypeFilter(e.target.value)}>
                        <option value="all">All Income Types</option>
                        <option value="salary">Salary</option>
                        <option value="business">Business (Proprietor)</option>
                        <option value="business_partnership">Business (Firm/LLP/Pvt Ltd)</option>
                    </select>
                </div>
            </div>

            {/* Table list */}
            <div className="table-card-v2">
                <div className="table-card-header-v2">
                    <h3>Incomplete Entries</h3>
                </div>

                <div className="table-responsive">
                    <table className="v2-table">
                        <thead>
                            <tr>
                                <th>Participant</th>
                                <th>Email & Contact</th>
                                <th>Income Type</th>
                                <th>Last Active</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentItems.length > 0 ? (
                                currentItems.map((report) => (
                                    <tr key={report.id || report.reportId}>
                                        <td>
                                            <div className="user-cell">
                                                <div className="user-avatar-small" style={{ background: '#F59E0B', color: 'white' }}>
                                                    {report.personalInfo?.name?.charAt(0).toUpperCase() || 'P'}
                                                </div>
                                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                    <span style={{ fontWeight: 600, color: 'var(--admin-v2-primary)' }}>{report.personalInfo?.name || 'Incomplete Profile'}</span>
                                                    <span style={{ fontSize: '11px', color: 'var(--admin-v2-text-muted)' }}>{report.personalInfo?.gender ? `${report.personalInfo.gender}, ${report.personalInfo.age || ''} yrs` : 'N/A'}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                <span style={{ fontSize: '13px' }}>{report.personalInfo?.email || 'N/A'}</span>
                                                <span style={{ fontSize: '11px', color: 'var(--admin-v2-text-muted)' }}>{report.personalInfo?.phone || 'N/A'}</span>
                                            </div>
                                        </td>
                                        <td>
                                            <span style={{ textTransform: 'capitalize', fontSize: '13px', fontWeight: 500 }}>
                                                {report.personalInfo?.incomeType?.replace('_', ' ') || 'Not Selected'}
                                            </span>
                                        </td>
                                        <td style={{ fontSize: '13px', color: '#475569' }}>
                                            {formatDate(report.updatedAt || report.createdAt)}
                                        </td>
                                        <td className="actions-cell">
                                            <div className="action-buttons-v2">
                                                <button
                                                    onClick={() => openDetailModal(report)}
                                                    className="icon-btn-v2 view"
                                                    title="View Filled Info"
                                                >
                                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(report.reportId)}
                                                    className="icon-btn-v2 delete"
                                                    title="Delete Draft"
                                                >
                                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5" style={{ textAlign: 'center', padding: '30px', color: '#64748b' }}>
                                        No incomplete submissions found matching current filters.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Controls */}
                {filteredReports.length > 0 && (
                    <div className="pagination-v2">
                        <div className="pagination-info-v2">
                            Showing <strong>{indexOfFirstItem + 1}</strong> to <strong>{Math.min(indexOfLastItem, filteredReports.length)}</strong> of <strong>{filteredReports.length}</strong> results
                        </div>
                        <div className="pagination-controls-v2">
                            <button
                                className="page-btn-v2"
                                onClick={() => paginate(currentPage - 1)}
                                disabled={currentPage === 1}
                                title="Previous Page"
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
                            </button>

                            <div className="page-numbers-v2" style={{ display: 'flex', gap: '4px' }}>
                                {[...Array(totalPages)].map((_, i) => (
                                    <button
                                        key={i}
                                        className={`page-btn-v2 ${currentPage === i + 1 ? 'active' : ''}`}
                                        onClick={() => paginate(i + 1)}
                                    >
                                        {i + 1}
                                    </button>
                                ))}
                            </div>

                            <button
                                className="page-btn-v2"
                                onClick={() => paginate(currentPage + 1)}
                                disabled={currentPage === totalPages || totalPages === 0}
                                title="Next Page"
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Detail Modal */}
            {showModal && selectedReport && (
                <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px' }}>
                    <div style={{ background: '#ffffff', width: '100%', maxWidth: '850px', maxHeight: '90vh', borderRadius: '12px', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)' }}>
                        
                        {/* Modal Header */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px', background: '#0EAB5EFF', color: '#ffffff' }}>
                            <div>
                                <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700 }}>Filled Information Details</h3>
                                <span style={{ fontSize: '12px', opacity: 0.9, fontFamily: 'monospace' }}>Report Draft ID: {selectedReport.reportId}</span>
                            </div>
                            <button onClick={closeDetailModal} style={{ background: 'transparent', border: 'none', color: '#ffffff', fontSize: '24px', cursor: 'pointer', outline: 'none' }}>&times;</button>
                        </div>

                        {/* Modal Body */}
                        <div style={{ padding: '24px', overflowY: 'auto', flex: 1 }}>
                            
                            {/* Personal Info */}
                            {renderDataSection("1. Personal Details", selectedReport.personalInfo)}

                            {/* Salary details */}
                            {renderDataSection("2. Income Details (Salary)", selectedReport.salaryIncome)}

                            {/* Business details */}
                            {renderDataSection("2. Business Core Details", selectedReport.businessDetails)}

                            {/* Salary Expenses */}
                            {renderDataSection("3. Expenses (Salary)", selectedReport.salaryExpenses)}

                            {/* Business Expenses */}
                            {renderDataSection("3. Expenses (Business)", selectedReport.businessExpenses)}

                            {/* Salary Investments */}
                            {renderDataSection("4. Investments (Salary)", selectedReport.salaryInvestment)}

                            {/* Business Investments */}
                            {renderDataSection("4. Investments (Business)", selectedReport.businessInvestment)}

                            {/* Joint Family (Couple) */}
                            {renderDataSection("Joint Financial Details (Couple)", selectedReport.jointSalary)}

                            {/* Land & Gold */}
                            {renderDataSection("5. Assets & Liabilities (Land & Gold)", selectedReport.landGold)}
                            
                            {/* Raw Tax Details */}
                            {renderDataSection("Tax Exemptions & Details", selectedReport.taxDetails)}

                        </div>

                        {/* Modal Footer */}
                        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '16px 24px', borderTop: '1px solid #e2e8f0', background: '#f8fafc' }}>
                            <button onClick={closeDetailModal} className="btn-secondary" style={{ padding: '8px 20px', borderRadius: '6px', fontWeight: 600 }}>Close</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default IncompleteReports;
